import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LoadingIndicator } from '../../Tools/LoadingIndicator';
import { useWorkspace } from '../../../context/WorkspaceContext';
import YoutubeLoginButton from '../../Buttons/Google/GoogleLoginButton';
import useThemeColors from '../../../hooks/useThemeColors';

// Import reusable components
import ChartCard from '../ChartCard';
import PlatformMetricCard from '../PlatformMetricCard';
import { SkeletonMetricCard, SkeletonTable, SkeletonChart, SkeletonDemographics } from '../AnalyticsSkeletons';
import { 
  FiUsers, 
  FiPlay, 
  FiHeart, 
  FiBarChart2, 
  FiVideo,
  FiAlertCircle,
  FiRefreshCw,
  FiClock,
  FiEye,
  FiMessageCircle,
  FiYoutube,
  FiTarget,
  FiUserPlus,
  FiTrendingUp
} from 'react-icons/fi';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const YouTubeAnalytics = () => {
  const { activeWorkspace, idToken } = useWorkspace();
  const navigate = useNavigate();
  const { isDark } = useThemeColors();

  // YouTube Analytics State
  const [youtubeAccounts, setYoutubeAccounts] = useState([]);
  const [selectedYoutubeAccount, setSelectedYoutubeAccount] = useState(null);
  const [youtubeAnalytics, setYoutubeAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  // API Functions
  const fetchYouTubeAccounts = useCallback(async () => {
    if (!activeWorkspace || !idToken) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/youtube/accounts`,
        {
          params: { workspaceId: activeWorkspace.id },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setYoutubeAccounts(response.data.accounts || []);
      
      // Auto-select first account if available
      if (response.data.accounts && response.data.accounts.length > 0) {
        setSelectedYoutubeAccount(response.data.accounts[0].platform_id);
      }
    } catch (error) {
      console.error('Error fetching YouTube accounts:', error);
      setAnalyticsError('Failed to load YouTube accounts. Please connect a YouTube account first.');
    }
  }, [activeWorkspace, idToken]);

  const fetchYouTubeAnalytics = useCallback(async (platformId, days = 30) => {
    if (!activeWorkspace || !idToken || !platformId) return;
    
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    
    try {
      const response = await axios.get(
        `${BACKEND_API_URL}/api/auth/user/analytics/youtube/analytics`,
        {
          params: { 
            workspaceId: activeWorkspace.id, 
            platform_id: platformId,
            days: days
          },
          headers: { Authorization: `Bearer ${idToken}` }
        }
      );
      
      setYoutubeAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching YouTube analytics:', error);
      if (error.response?.status === 401) {
        setAnalyticsError('YouTube token expired. Please reconnect your YouTube account.');
      } else if (error.response?.status === 404) {
        setAnalyticsError('YouTube channel not found. Please check your account connection.');
      } else if (error.response?.status === 429) {
        setAnalyticsError('YouTube API quota exceeded. Please try again later.');
      } else {
        setAnalyticsError('Failed to load YouTube analytics. Please try again.');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeWorkspace, idToken]);

  // Fetch YouTube accounts when component mounts
  useEffect(() => {
    if (activeWorkspace && idToken) {
      fetchYouTubeAccounts();
    }
  }, [fetchYouTubeAccounts, activeWorkspace, idToken]);

  // Fetch YouTube analytics when account or days selection changes
  useEffect(() => {
    if (selectedYoutubeAccount) {
      fetchYouTubeAnalytics(selectedYoutubeAccount, selectedDays);
    }
  }, [selectedYoutubeAccount, selectedDays, fetchYouTubeAnalytics]);

  // Filter posts based on selected time period
  const getFilteredPosts = () => {
    if (!youtubeAnalytics?.posts) return [];
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (selectedDays * 24 * 60 * 60 * 1000));
    
    return youtubeAnalytics.posts.filter(post => {
      const postDate = new Date(post.published_at);
      return postDate >= cutoffDate;
    });
  };

  const filteredPosts = getFilteredPosts();

  return (
    <>
      {/* YouTube Account Selection */}
      {youtubeAccounts.length > 1 && (
        <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-4 --theme-colors">Select YouTube Account</h3>
          <div className="flex flex-wrap gap-3">
            {youtubeAccounts.map((account) => (
              <button
                key={account.platform_id}
                onClick={() => setSelectedYoutubeAccount(account.platform_id)}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg border transition-colors ${
                  selectedYoutubeAccount === account.platform_id
                    ? isDark
                      ? 'border-red-400 bg-red-900/30 text-red-300'
                      : 'border-red-500 bg-red-50 text-red-700'
                    : isDark
                      ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {account.logo && (
                  <img 
                    src={account.logo} 
                    alt={account.username}
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full"
                  />
                )}
                <span className="font-medium text-sm sm:text-base">{account.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {analyticsError && (
        <div className={`rounded-lg p-4 sm:p-6 border ${
          isDark
            ? 'bg-red-900/30 border-red-700'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <FiAlertCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
            <p className={isDark ? 'text-red-300' : 'text-red-800'}>{analyticsError}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <button
              onClick={() => fetchYouTubeAccounts()}
              className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'text-red-400 hover:text-red-300 border-red-700 hover:bg-red-900/20'
                  : 'text-red-600 hover:text-red-700 border-red-300 hover:bg-red-100'
              }`}
            >
              <FiRefreshCw className="w-4 h-4" />
              Retry
            </button>
            {analyticsError.includes('connect') && (
              <div className="w-full sm:w-auto sm:max-w-xs">
                <YoutubeLoginButton />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Content */}
      {(selectedYoutubeAccount || analyticsLoading) && !analyticsError && (
        <>
          {/* Account Display & Period Selection */}
          {!analyticsLoading && youtubeAnalytics && (
            <div className="--theme-colors rounded-lg shadow-md p-4 sm:p-6 mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Account Info - Left Side */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isDark
                      ? 'bg-gradient-to-r from-red-600 to-red-700'
                      : 'bg-gradient-to-r from-red-500 to-red-600'
                  }`}>
                    <FiYoutube className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold --theme-colors">
                      {youtubeAccounts.find(acc => acc.platform_id === selectedYoutubeAccount)?.username || 'YouTube Channel'}
                    </h3>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Analytics for {youtubeAccounts.find(acc => acc.platform_id === selectedYoutubeAccount)?.username || 'channel'} • Last {selectedDays} days
                    </p>
                  </div>
                </div>
                
                {/* Period Selection - Right Side */}
                <div className="flex flex-wrap gap-2">
                  {[7, 30, 60, 90].map((days) => (
                    <button
                      key={days}
                      onClick={() => setSelectedDays(days)}
                      className={`px-3 sm:px-4 py-2 rounded-lg border transition-colors text-xs sm:text-sm ${
                        selectedDays === days
                          ? isDark
                            ? 'border-red-400 bg-red-900/30 text-red-300'
                            : 'border-red-500 bg-red-50 text-red-700'
                          : isDark
                            ? 'border-gray-700 hover:border-gray-600 text-gray-300'
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      Last {days} days
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* All Metrics in Single Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            {analyticsLoading ? (
              <>
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
                <SkeletonMetricCard />
              </>
            ) : youtubeAnalytics ? (
              <>
                <PlatformMetricCard
                  title="Subscribers"
                  value={youtubeAnalytics.overview.total_subscribers?.toLocaleString() || '0'}
                  icon={FiUsers}
                  platformColor="red"
                  subtitle="Total"
                />
                <PlatformMetricCard
                  title="Avg Views"
                  value={youtubeAnalytics.overview.avg_views?.toLocaleString() || '0'}
                  icon={FiEye}
                  platformColor="red"
                  subtitle="Per video"
                />
                <PlatformMetricCard
                  title="Total Views"
                  value={youtubeAnalytics.overview.total_views?.toLocaleString() || '0'}
                  icon={FiPlay}
                  platformColor="red"
                  subtitle="Channel total"
                />
                <PlatformMetricCard
                  title="Total Videos"
                  value={youtubeAnalytics.overview.total_videos?.toLocaleString() || '0'}
                  icon={FiVideo}
                  platformColor="blue"
                  subtitle="Published"
                />
              </>
            ) : null}
          </div>

          {/* Recent Videos Table */}
          {(analyticsLoading || youtubeAnalytics) && (
            <ChartCard
              title="Recent Videos Performance"
              description={analyticsLoading ? "Loading videos..." : `Showing ${filteredPosts?.length || 0} videos from the last ${selectedDays} days`}
              icon={FiBarChart2}
            >
              {analyticsLoading ? (
                <SkeletonTable />
              ) : filteredPosts && filteredPosts.length > 0 ? (
              <div className="space-y-4">
                {/* Videos Summary Metrics */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-3">
                        <FiHeart className="w-5 h-5 text-red-600" />
                        <div>
                          <p className="text-sm font-medium text-red-700">Overall Engagement</p>
                          <p className="text-xl font-bold text-red-900">
                            {youtubeAnalytics.overview.engagement_rate ? `${youtubeAnalytics.overview.engagement_rate}%` : '0%'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <FiMessageCircle className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="text-sm font-medium text-orange-700">Avg Comments</p>
                          <p className="text-xl font-bold text-orange-900">
                            {youtubeAnalytics.overview.avg_comments?.toLocaleString() || '0'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Videos Analyzed</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {filteredPosts.length}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Video</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Category</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Views</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Likes</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Comments</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-600">Engagement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPosts.map((video, index) => (
                        <tr key={video.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              {video.thumbnail_url && (
                                <img 
                                  src={video.thumbnail_url} 
                                  alt="Video thumbnail"
                                  className="w-12 h-8 rounded object-cover"
                                />
                              )}
                              <div className="max-w-xs">
                                <p className="text-sm font-medium truncate">
                                  {video.title || 'No title'}
                                </p>
                                <p className="text-xs text-gray-500 truncate mt-1">
                                  {video.description}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                              {video.category || 'Other'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {new Date(video.published_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium">
                            {video.views?.toLocaleString() || '0'}
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium">
                            {video.likes?.toLocaleString() || '0'}
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-medium">
                            {video.comments?.toLocaleString() || '0'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className={`text-sm font-medium ${
                              video.engagement_rate && parseFloat(video.engagement_rate) > 3 
                                ? 'text-green-600' : 'text-gray-600'
                            }`}>
                              {video.engagement_rate ? `${video.engagement_rate}%` : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              </div>
              ) : (
                <div className="text-center py-12">
                  <FiVideo className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No videos in the last {selectedDays} days</h3>
                  <p className="text-gray-500">
                    Try selecting a longer time period or publish some content to see your performance metrics.
                  </p>
                </div>
              )}
            </ChartCard>
          )}

          {/* Daily Insights Charts */}
          {(analyticsLoading || youtubeAnalytics?.insights) && (
            <div className="space-y-6">
              {/* Daily Analytics - Full Width */}
              {(analyticsLoading || (youtubeAnalytics?.insights?.daily_analytics && youtubeAnalytics.insights.daily_analytics.length > 0)) && (
                <ChartCard
                  title="Daily Performance"
                  description={analyticsLoading ? "Loading daily analytics..." : "Your daily views, impressions, and watch time over the selected period"}
                  icon={FiBarChart2}
                >
                  {analyticsLoading ? (
                    <SkeletonChart />
                  ) : (
                  <div className="h-64 bg-gradient-to-r from-red-50 to-red-100 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <FiBarChart2 className="w-12 h-12 text-red-400 mx-auto mb-2" />
                      <p className="text-red-600 font-medium">Daily Analytics Chart</p>
                      <p className="text-sm text-red-500 mt-1">
                        {youtubeAnalytics.insights.daily_analytics?.length || 0} data points
                      </p>
                      <div className="flex justify-center gap-6 mt-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">
                            {youtubeAnalytics.insights.daily_analytics.reduce((sum, day) => sum + day.views, 0)?.toLocaleString()}
                          </div>
                          <div className="text-sm text-red-500">Total Views</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-600">
                            {youtubeAnalytics.insights.daily_analytics.reduce((sum, day) => sum + day.impressions, 0)?.toLocaleString()}
                          </div>
                          <div className="text-sm text-orange-500">Total Impressions</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">
                            {Math.round(youtubeAnalytics.insights.daily_analytics.reduce((sum, day) => 
                              sum + day.estimated_minutes_watched, 0) / 60)?.toLocaleString()}
                          </div>
                          <div className="text-sm text-blue-500">Hours Watched</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </ChartCard>
              )}

              {/* Subscriber Changes - Full Width */}
              {(analyticsLoading || (youtubeAnalytics?.insights?.daily_subscriber_changes && youtubeAnalytics.insights.daily_subscriber_changes.length > 0)) && (
                <ChartCard
                  title="Daily Subscriber Changes"
                  description={analyticsLoading ? "Loading subscriber data..." : "Track your daily subscriber gains and losses"}
                  icon={FiUserPlus}
                >
                  {analyticsLoading ? (
                    <SkeletonChart />
                  ) : (
                  <div className="h-64 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <FiUserPlus className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-green-600 font-medium">Subscriber Changes Chart</p>
                      <p className="text-sm text-green-500 mt-1">
                        {youtubeAnalytics.insights.daily_subscriber_changes?.length || 0} data points
                      </p>
                      <div className="flex justify-center gap-6 mt-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-600">
                            {youtubeAnalytics.insights.daily_subscriber_changes.reduce((sum, day) => sum + day.subscribers_gained, 0)}
                          </div>
                          <div className="text-sm text-green-500">Total Gained</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-red-600">
                            {youtubeAnalytics.insights.daily_subscriber_changes.reduce((sum, day) => sum + day.subscribers_lost, 0)}
                          </div>
                          <div className="text-sm text-red-500">Total Lost</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-blue-600">
                            {youtubeAnalytics.insights.daily_subscriber_changes.reduce((sum, day) => sum + day.net_growth, 0)}
                          </div>
                          <div className="text-sm text-blue-500">Net Growth</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                </ChartCard>
              )}
            </div>
          )}

          {/* Audience Demographics */}
          {(analyticsLoading || (youtubeAnalytics?.audience?.demographics && youtubeAnalytics.audience.demographics.length > 0)) && (
            <ChartCard
              title="Audience Demographics"
              description={analyticsLoading ? "Loading demographics..." : "Your audience breakdown by age and gender"}
              icon={FiUsers}
            >
              {analyticsLoading ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3 animate-pulse">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-2 bg-gray-200 rounded flex-1"></div>
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Viewer Demographics</h4>
                  <div className="space-y-2">
                    {youtubeAnalytics.audience.demographics
                      .sort((a, b) => b.percentage - a.percentage)
                      .slice(0, 8)
                      .map((demo, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">
                            {demo.gender === 'male' ? '♂' : '♀'} {demo.age_group}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-red-500 rounded-full" 
                                style={{ width: `${demo.percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium">{demo.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              )}
            </ChartCard>
          )}
        </>
      )}

      {/* No Data State */}
      {!youtubeAnalytics && !analyticsLoading && !analyticsError && youtubeAccounts.length === 0 && (
        <div className="text-center py-12">
          <FiTarget className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No YouTube Account Connected</h3>
          <p className="text-gray-500 mb-6">
            Connect your YouTube channel to view detailed analytics and performance metrics.
          </p>
          <button
            onClick={() => navigate('/socials')}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Connect YouTube Account
          </button>
        </div>
      )}
    </>
  );
};

export default YouTubeAnalytics;